/* Alpaca Apparel: native nested gift-box lines. No automatic POST retries. */
(() => {
  'use strict';
  const BOX = '58196363379070';
  const ROOT = window.Shopify.routes.root;
  const message = 'Gift-box quantities or links need updating. Adjust the affected wrap quantity, or remove the affected wrap and any unlinked boxes, then add it again with packaging.';
  let queue = Promise.resolve();
  let pending = 0;
  let checkingOut = false;

  class CartError extends Error {
    constructor(text, uncertain = false) {
      super(text);
      this.uncertain = uncertain;
    }
  }

  async function request(url, options = {}) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch(url, {
        ...options, signal: controller.signal, cache: 'no-store',
        headers: { Accept: 'application/json', ...options.headers },
      });
      const data = await response.json();
      if (!response.ok || data.status || data.errors) {
        throw new CartError(typeof data.description === 'string' ? data.description :
          typeof data.errors === 'string' ? data.errors : data.message || 'The cart could not be updated.');
      }
      return data;
    } catch (error) {
      if (error instanceof CartError) throw error;
      throw new CartError(options.method === 'POST'
        ? 'We could not confirm the update. Open your cart before adding again.'
        : 'We could not check your cart. Refresh the page and try again.', options.method === 'POST');
    } finally {
      clearTimeout(timeout);
    }
  }

  function serial(task) {
    pending++;
    document.documentElement.classList.add('alpaca-cart-busy');
    const run = queue.then(() => navigator.locks
      ? navigator.locks.request('alpaca-cart-write', task) : task());
    queue = run.catch(() => {});
    return run.finally(() => {
      pending--;
      if (!pending) document.documentElement.classList.remove('alpaca-cart-busy');
    });
  }

  const getCart = () => request(`${ROOT}cart.js`);
  const role = (item) => item.properties?._alpaca_gift_role;
  const group = (item) => item.properties?._alpaca_gift_group;
  const isBox = (item) => String(item.variant_id || item.id) === BOX;
  const parentKey = (item) => item.parent_relationship?.parent_key;

  function validate(cart) {
    const problems = [];
    const parents = cart.items.filter((item) => role(item) === 'wrap');
    for (const parent of parents) {
      const children = cart.items.filter((item) => isBox(item) && parentKey(item) === parent.key);
      if (!group(parent) || parent.product_type !== 'Wraps' ||
          parent.properties['Gift packaging'] !== 'Luxury Gift Box' || children.length !== 1 ||
          children[0].quantity !== parent.quantity || group(children[0]) !== group(parent) ||
          role(children[0]) !== 'box') problems.push(parent.key);
    }
    for (const child of cart.items.filter(isBox)) {
      const parent = parents.find((item) => item.key === parentKey(child));
      if (!parent || group(parent) !== group(child) || role(child) !== 'box') problems.push(child.key);
    }
    // Existing carts from the old implementation need explicit customer review.
    for (const item of cart.items) {
      if (!isBox(item) && item.properties?.['Gift packaging'] === 'Luxury Gift Box' &&
          role(item) !== 'wrap') problems.push(item.key);
    }
    return [...new Set(problems)];
  }

  async function sections(ids) {
    const url = new URL(window.location.href);
    url.searchParams.delete('section_id');
    url.searchParams.set('sections', [...new Set(ids)].join(','));
    return request(url.toString());
  }

  function jsonPost(path, body) {
    return request(`${ROOT}${path}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
  }

  function makeGiftData(original, giftId, title, token) {
    const data = new FormData();
    // Preserve properties, selling plans and uploaded files from the original product form.
    for (const [key, value] of original.entries()) {
      if (key === 'id' || key === 'quantity' || key === 'selling_plan') data.append(`items[0][${key}]`, value);
      else if (key.startsWith('properties[')) data.append(`items[0][properties]${key.slice(10)}`, value);
    }
    const quantity = original.get('quantity') || '1';
    if (!original.has('quantity')) data.set('items[0][quantity]', quantity);
    data.set('items[0][properties][Gift packaging]', 'Luxury Gift Box');
    data.set('items[0][properties][_alpaca_gift_role]', 'wrap');
    data.set('items[0][properties][_alpaca_gift_group]', token);
    data.set('items[1][id]', giftId);
    data.set('items[1][quantity]', quantity);
    data.set('items[1][parent_id]', original.get('id'));
    data.set('items[1][properties][_alpaca_gift_role]', 'box');
    data.set('items[1][properties][_alpaca_gift_group]', token);
    data.set('items[1][properties][Gift box for]', title);
    return data;
  }

  async function add(original, checkbox, ids, title) {
    return serial(async () => {
      const token = checkbox?.checked ? crypto.randomUUID() : null;
      const data = token ? makeGiftData(original, checkbox.dataset.giftVariantId, title, token) : original;
      if (ids.length) {
        data.set('sections', ids.join(','));
        data.set('sections_url', window.location.pathname);
      }
      const added = await request(`${ROOT}cart/add.js`, { method: 'POST', body: data });
      // A successful write is never retried because rendering or verification failed.
      let cart;
      try { cart = await getCart(); }
      catch (_) { return { ...added, added: true, needsReview: true }; }
      const selected = token
        ? cart.items.find((item) => group(item) === token && role(item) === 'wrap')
        : added.items?.[0] || added;
      return { ...added, key: selected?.key, id: selected?.id,
        added: true, needsReview: (token && !selected) || validate(cart).length > 0 };
    });
  }

  function resolve(cart, identity) {
    return cart.items.find((item) => item.key === identity.key) ||
      (identity.group && cart.items.find((item) => group(item) === identity.group && role(item) === identity.role));
  }

  async function changeQuantity(identity, quantity, ids) {
    return serial(async () => {
      quantity = Number(quantity);
      if (!Number.isSafeInteger(quantity) || quantity < 0) throw new CartError('Enter a valid quantity.');
      let cart = await getCart();
      let target = resolve(cart, identity);
      if (!target) throw new CartError('Your cart has changed. Refresh it before updating this item.');
      if (isBox(target) && parentKey(target) && role(target) === 'box') {
        throw new CartError('Change the wrap quantity to update its gift boxes.');
      }
      let failure;
      try {
        const child = cart.items.find((item) => isBox(item) && parentKey(item) === target.key);
        // Reduce the child first; increase the parent first. Inventory is checked by change.js.
        if (child && quantity > 0 && quantity < target.quantity && child.quantity !== quantity) {
          cart = await jsonPost('cart/change.js', { id: child.key, quantity });
          target = resolve(cart, identity);
        }
        cart = await jsonPost('cart/change.js', { id: target.key, quantity });
        target = resolve(cart, identity);
        const currentChild = target && cart.items.find((item) => isBox(item) && parentKey(item) === target.key);
        if (quantity > 0 && currentChild && currentChild.quantity !== target.quantity) {
          cart = await jsonPost('cart/change.js', { id: currentChild.key, quantity: target.quantity });
        }
      } catch (error) { failure = error.message; }
      // Re-read even after an error: a request can have changed the cart before failing.
      cart = await getCart();
      cart.alpacaError = failure || (validate(cart).length ? message : '');
      cart.sections = await sections(ids);
      return cart;
    });
  }

  function showError(text) {
    const surfaces = document.querySelectorAll('#cart-errors, #CartDrawer-CartErrors');
    surfaces.forEach((element) => { element.textContent = text; element.setAttribute('role', 'alert'); });
    if (!surfaces.length) window.location.assign(window.routes.cart_url);
  }

  async function checkout(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkingOut) return;
    if ([...document.querySelectorAll('cart-items .quantity__input, cart-drawer-items .quantity__input')]
      .some((input) => input.value !== input.defaultValue)) {
      showError('Please let your quantity update finish before checking out.');
      return;
    }
    checkingOut = true;
    try {
      await serial(async () => {
        const cart = await getCart();
        if (validate(cart).length) throw new CartError(message);
        const noteField = document.querySelector('cart-note textarea');
        if (noteField && noteField.value !== (cart.note || '')) {
          await jsonPost('cart/update.js', { note: noteField.value });
        }
        // Do not submit stale updates[] fields, which could undo synchronisation.
        window.location.assign(`${ROOT}checkout`);
      });
    } catch (error) { showError(error.message); }
    finally { checkingOut = false; }
  }

  document.addEventListener('click', (event) => {
    if (event.target.closest('[name="checkout"], a[href="/checkout"], a[href="' + ROOT + 'checkout"]')) checkout(event);
  }, true);
  document.addEventListener('submit', (event) => {
    if (event.target.matches('#cart, #CartDrawer-Form')) checkout(event);
  }, true);

  window.AlpacaGiftCart = { add, changeQuantity, getCart, validate, makeGiftData,
    get busy() { return pending > 0; } };
})();
